import { useTranslation } from 'react-i18next';
import * as Popover from '@radix-ui/react-popover';
import type { Me, Project } from '@jisy/shared';
import { useLogoutMutation } from '../api/queries.js';
import { Avatar, SectionMark } from './Badges.js';
import { LanguagePicker } from './LanguagePicker.js';
import { Logo } from './Logo.js';
import { NotificationsBell } from './NotificationsBell.js';
import { WorkspaceMenu } from './WorkspaceMenu.js';
import styles from './AppHeader.module.css';

/** Верхняя полоса: марка, адрес сайта, выбранный раздел и меню пользователя. */
export function AppHeader({
  me,
  project,
  onOpenSettings,
  onOpenIssue,
}: {
  me: Me;
  /** Выбранный раздел; null — режим «Все задачи» или вид, в котором разделов нет. */
  project: Project | null;
  onOpenSettings: () => void;
  /** Уведомление ведёт в карточку задачи, а карточкой владеет страница, а не шапка. */
  onOpenIssue: (issueKey: string) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const logout = useLogoutMutation();

  return (
    <header className={styles.header}>
      <Logo />

      {/*
        Адрес сайта — он же переключатель пространств. Название сайта рядом не показываем:
        оно и так стоит первым в адресе, а в списке пространств названо полностью.
      */}
      <WorkspaceMenu me={me} />

      {/*
        Выбранный раздел ссылкой на его проект в Jira. Стоит рядом с адресом сайта, потому что
        читается как продолжение адреса: сайт → раздел. Ключ ведёт на /browse — Jira сама
        решает, показать доску, бэклог или список, в зависимости от типа проекта.
      */}
      {project && (
        <a
          className={styles.sectionLink}
          href={`${me.tenant.siteUrl}/browse/${project.key}`}
          target="_blank"
          rel="noreferrer noopener"
          title={t('sections.openInJira', { name: project.name })}
        >
          <SectionMark colorIndex={project.colorIndex} />
          <span className={`${styles.sectionName} truncate`}>{project.name}</span>
          <span aria-hidden="true">↗</span>
          <span className="visually-hidden">{t('sections.openInJira', { name: project.name })}</span>
        </a>
      )}

      <div className={styles.spacer} />

      {/* При отозванном доступе уведомлений быть не может: они читаются прямо из Jira. */}
      {me.tenant.status === 'active' && <NotificationsBell onOpenIssue={onOpenIssue} />}

      <LanguagePicker authenticated />

      <button type="button" className={styles.settingsButton} onClick={onOpenSettings}>
        {t('app.settings')}
      </button>

      <Popover.Root>
        <Popover.Trigger asChild>
          <button type="button" className={styles.avatarButton} aria-label={me.user.displayName}>
            <Avatar user={me.user} size={26} strong />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content className={styles.menu} align="end" sideOffset={6}>
            <div className={styles.menuUser}>
              <Avatar user={me.user} size={26} strong />
              <div className={styles.menuUserText}>
                <span className={styles.menuUserName}>{me.user.displayName}</span>
                {me.user.email && <span className={styles.menuUserEmail}>{me.user.email}</span>}
              </div>
            </div>
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
            >
              {t('app.logout')}
            </button>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </header>
  );
}
